import { AxisLeft, AxisBottom } from '@vx/axis'
import { format } from 'd3-format'
import { scaleLinear, scaleTime } from 'd3-scale'
import { timeFormat } from 'd3-time-format'
import { transparentize } from 'polished'
import React, { Key, useMemo } from 'react'

import ChartTooltip, { useChartTooltip } from 'components/charts/ChartTooltip'
import Grid from 'components/charts/Grid'
import Group from 'components/charts/Group'
import Text from 'components/common/Text'
import Line from 'components/reports/common/Line'
import { LineChartSVG } from 'components/reports/common/LineChartSVG'
import { LineChartSVGWrapper } from 'components/reports/common/LineChartSVGWrapper'
import {
  DEFAULT_LINE_CHART_HEIGHT,
  DEFAULT_LINE_CHART_WIDTH,
  DEFAULT_NUMBER_OF_COLUMNS,
  DEFAULT_NUMBER_OF_ROWS,
  DEFAULT_NUMBER_OF_TICKS_X_AXIS,
  DEFAULT_NUMBER_OF_TICKS_Y_AXIS,
  getYAxisTickValues,
  getYMaxFromData,
  getYMinFromData,
  LineChartDataPoint,
  LineChartDomain
} from 'components/reports/report-utils'
import theme from 'theme'
import { last } from 'utils/list'

const formatWholeNumber = format('d')
const formatDate = timeFormat('%b')

export const RMLineChart: React.FC<{
  title?: string
  darkMode?: boolean
  data: LineChartDataPoint[]
  dataSeries: string[]
  domain?: LineChartDomain
  hasColorBandBackground?: boolean
  height?: number
  lineColor: string
  numOfColumns?: number
  numOfRows?: number
  numOfTicksXAxis?: number
  numOfTicksYAxis?: number
  showGridLines?: boolean
  showXAxis?: boolean
  showYAxis?: boolean
  width?: number
  xAxisLabel?: string
  xAxisLabelOffset?: number
  xAxisTickLabelProps?: Record<string, string | number>
  xKey: string
  xValueType?: string
  yAxisLabel?: string
  yAxisLabelOffset?: number
  yAxisTickLabelProps?: Record<string, string | number>
  yKey: string
  yValueType?: string
}> = ({
  title,
  darkMode,
  data,
  dataSeries,
  domain,
  lineColor,
  hasColorBandBackground,
  height = DEFAULT_LINE_CHART_HEIGHT,
  numOfColumns = DEFAULT_NUMBER_OF_COLUMNS,
  numOfRows = DEFAULT_NUMBER_OF_ROWS,
  numOfTicksXAxis = DEFAULT_NUMBER_OF_TICKS_X_AXIS,
  numOfTicksYAxis = DEFAULT_NUMBER_OF_TICKS_Y_AXIS,
  showGridLines = true,
  showXAxis = true,
  showYAxis = true,
  width = DEFAULT_LINE_CHART_WIDTH,
  xAxisLabel,
  xAxisLabelOffset = 36, // matches vx AxisBottom default value
  xAxisTickLabelProps = {},
  xKey,
  xValueType = 'time', // default to "time" for the first pass
  yAxisLabel,
  yAxisLabelOffset = 8, // matches vs AxisLeft default value
  yAxisTickLabelProps = {},
  yKey,
  yValueType = 'linear', // default to "linear" for the first pass
  ...rest
}) => {
  /**
   * This is not utilized on the first pass through the RM Dashboard improvements, but is left in for future expansion.
   *
   * TODO: Implement data point tooltips
   */
  const { tooltipConfig, setTargetTooltip, clearTargetTooltip } =
    useChartTooltip({
      offsetTop: -theme.space[3], // aligns the tooltip about center line vertially
      // @ts-ignore TODO: Refactor useChartTooltip hook to use TS
      offsetLeft: theme.space[1]
    })

  /**
   * Dimension calculations
   *
   * These dimensions were chosen because they scale the svg document in a way that
   * looks good in our various layouts
   */
  const margin = { top: 20, right: 45, bottom: 30, left: 45 }
  const xMax = width - margin.left - margin.right
  const yMin = domain?.y[0] || getYMinFromData(data, dataSeries)
  const yMax = domain?.y[1] || getYMaxFromData(data, dataSeries)

  // colors
  const labelColor = darkMode ? theme.colors.steel[0] : theme.colors.steel[1]
  const gridColor = transparentize(0.75, theme.colors.steel[1])
  const graphBorderColor = transparentize(0.25, theme.colors.steel[1])

  /**
   * Creates the d3 scale function for the independent axis values.
   *
   * TODO: As the use of this chart expands, more "xValueType" cases will need to be added
   */
  const xScale = useMemo(() => {
    if (xValueType === 'time') {
      return scaleTime()
        .domain(
          domain?.x || [
            new Date(data[0][xKey]),
            new Date(data[data.length - 1][xKey])
          ]
        )
        .range([0, xMax])
    }
  }, [data, domain?.x, xKey, xMax, xValueType])

  /**
   * Creates the d3 scale function for the dependent axis values.
   *
   * TODO: As the use of this chart expands, more "yValueType" cases will need to be added
   */
  const yScale = useMemo(() => {
    if (yValueType === 'linear') {
      const yTicks = getYAxisTickValues(yMin, yMax, numOfTicksYAxis)

      return scaleLinear()
        .domain(domain?.y || [yTicks[0], last(yTicks)])
        .range([height - (margin.top + margin.bottom), 0])
        .nice()
    }
  }, [
    domain?.y,
    height,
    margin.bottom,
    margin.top,
    numOfTicksYAxis,
    yMax,
    yMin,
    yValueType
  ])

  return (
    <LineChartSVGWrapper
      data-test-rm-line-chart
      css={{
        backgroundColor: darkMode
          ? theme.colors.steel[2]
          : theme.colors.white[0],
        width: 'auto' // Override default width to respond better within OffCanvas and on-page contexts
      }}
      {...rest}
    >
      {title && (
        <Text
          data-test-chart-title
          as="h3"
          css={{
            color: darkMode ? theme.colors.steel[0] : theme.colors.steel[2],
            paddingBottom: theme.space[3]
          }}
        >
          {title}
        </Text>
      )}
      <LineChartSVG
        ref={tooltipConfig.boundingElRef}
        height={height}
        width={width}
      >
        <Grid
          hasColorBandBackground={hasColorBandBackground}
          height={height - margin.top - margin.bottom}
          left={margin.left}
          numTicksColumn={numOfColumns}
          numTicksRow={numOfRows}
          stroke={showGridLines ? gridColor : undefined}
          strokeDasharray="2,2"
          top={margin.top}
          width={xMax}
          xScale={xScale}
          yScale={yScale}
        />
        <Group left={margin.left} top={margin.top}>
          <Group
            clipPath="url(#RelevantDataClip)"
            css={{
              '--circle-opacity': 0.5,
              '&:hover': { '--circle-opacity': 1 }
            }}
          >
            {data.map((dataPoint) => {
              return (
                <Line
                  key={dataPoint[xKey] as Key}
                  clearTargetTooltip={clearTargetTooltip}
                  color={lineColor}
                  data={data}
                  dataPoint={dataPoint}
                  setTargetTooltip={setTargetTooltip}
                  x={xKey}
                  xScale={xScale}
                  y={yKey}
                  yMax={yMax as number}
                  yScale={yScale}
                />
              )
            })}
          </Group>
        </Group>
        <Group left={margin.left}>
          {showYAxis && (
            <AxisLeft
              label={yAxisLabel}
              labelOffset={yAxisLabelOffset}
              left={0}
              numTicks={numOfTicksYAxis}
              scale={yScale}
              stroke={graphBorderColor}
              tickComponent={({ formattedValue, ...tickProps }) => (
                <text {...tickProps}>{formattedValue}</text>
              )}
              tickFormat={formatWholeNumber}
              tickLabelProps={() => ({
                fill: labelColor,
                textAnchor: 'end',
                fontSize: 12,
                dx: '0.2em',
                dy: '0.1em',
                ...yAxisTickLabelProps
              })}
              tickStroke="transparent"
              top={margin.top}
            />
          )}
          {showXAxis && (
            <AxisBottom
              label={xAxisLabel}
              labelOffset={xAxisLabelOffset}
              left={0}
              numTicks={numOfTicksXAxis}
              scale={xScale}
              stroke={graphBorderColor}
              tickComponent={({ formattedValue, ...tickProps }) => (
                <text {...tickProps}>{formattedValue}</text>
              )}
              tickFormat={formatDate}
              tickLabelProps={() => ({
                fill: labelColor,
                textAnchor: 'end',
                fontSize: theme.fontSizes[0],
                dx: '2.5em',
                dy: '0.1em',
                ...xAxisTickLabelProps
              })}
              tickStroke="transparent"
              top={height - margin.bottom}
            />
          )}
        </Group>
      </LineChartSVG>
      <ChartTooltip darkMode={darkMode} tooltipConfig={tooltipConfig} />
    </LineChartSVGWrapper>
  )
}
